import { LosslessNumber } from "lossless-json"
import type { UxPage } from "./model"
import { rendererNotice, type Notice } from "./messages"

/** A page's decoded context: typed values, or why a supplied parameter cannot be used. */
export type DecodedContext = { readonly ok: true; readonly values: Readonly<Record<string, unknown>> } | { readonly ok: false; readonly problems: readonly string[] }

const INTEGER = /^-?(0|[1-9]\d*)$/
const DECIMAL = /^-?(0|[1-9]\d*)(\.\d+)?([eE][-+]?\d+)?$/
const DATE = /^\d{4}-\d{2}-\d{2}$/
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/
const DURATION = /^P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d)(\d+H)?(\d+M)?(\d+(\.\d+)?S)?)?$/

/**
 * Decode the raw query text against the page's declared context parameters. Numbers keep their exact
 * digits (`LosslessNumber`), Booleans are `true`/`false` only, temporal values must be ISO text, and
 * JSON parameters are refused: the UX contract supplies those through typed navigation, not the URL.
 */
export function decodeContextNotices(page: UxPage, raw: Readonly<Record<string, string>>): { readonly ok: true; readonly values: Readonly<Record<string, unknown>> } | { readonly ok: false; readonly problems: readonly Notice[] } {
  const values: Record<string, unknown> = {}
  const problems: Notice[] = []
  for (const param of page.contextParams) {
    const text = raw[param.paramId]
    if (text === undefined || text === "") {
      if (param.required) problems.push({ key: "context.required", values: { paramId: param.paramId } })
      continue
    }
    switch (param.type) {
      case "text":
        values[param.paramId] = text
        break
      case "integer":
        if (INTEGER.test(text)) values[param.paramId] = new LosslessNumber(text)
        else problems.push({ key: "context.integer", values: { paramId: param.paramId } })
        break
      case "number":
        if (DECIMAL.test(text)) values[param.paramId] = new LosslessNumber(text)
        else problems.push({ key: "context.number", values: { paramId: param.paramId } })
        break
      case "boolean":
        if (text === "true" || text === "false") values[param.paramId] = text === "true"
        else problems.push({ key: "context.booleanLiteral", values: { paramId: param.paramId } })
        break
      case "date":
        if (DATE.test(text)) values[param.paramId] = text
        else problems.push({ key: "context.date", values: { paramId: param.paramId } })
        break
      case "timestamp":
        if (TIMESTAMP.test(text)) values[param.paramId] = text
        else problems.push({ key: "context.timestamp", values: { paramId: param.paramId } })
        break
      case "duration":
        if (DURATION.test(text)) values[param.paramId] = text
        else problems.push({ key: "context.duration", values: { paramId: param.paramId } })
        break
      case "json":
        problems.push({ key: "context.jsonNavigation", values: { paramId: param.paramId } })
        break
    }
  }
  return problems.length > 0 ? { ok: false, problems } : { ok: true, values }
}

/** Render-time convenience for hosts that decode on every render. */
export function decodeContext(page: UxPage, raw: Readonly<Record<string, string>>, locale = "en"): DecodedContext {
  const decoded = decodeContextNotices(page, raw)
  return decoded.ok ? decoded : { ok: false, problems: decoded.problems.map((notice) => rendererNotice(notice, locale)) }
}

/** Canonical URL text for a context value (exact digits for numbers). */
export function encodeContextValue(value: unknown): string | null {
  if (value instanceof LosslessNumber) return value.value
  if (typeof value === "string") return value
  if (typeof value === "boolean") return value ? "true" : "false"
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return null
}
